import React, { useState, useRef, useEffect } from 'react';
import axios, { getAuthToken } from '../utils/axiosInstance';
import '../App.css';

// Component imports
import Layout from '../components/Layout';
import { usePractice } from '../context/PracticeContext';

// API base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const ChatPage = () => {
  // Multi-tenant: the chat tests against the currently-selected practice.
  const { practiceId } = usePractice();

  // State variables
  const [sessionId, setSessionId] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Refs
  const conversationEndRef = useRef(null);
  const inputRef = useRef(null);
  // Mirrors `sessionId` for event handlers (pagehide) that capture once on mount.
  const sessionIdRef = useRef(null);

  // Auto-scroll to bottom of conversation when it updates
  useEffect(() => {
    if (conversationEndRef.current) {
      conversationEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation]);

  // Focus input on mount and after loading completes
  useEffect(() => {
    if (inputRef.current && !loading) {
      inputRef.current.focus();
    }
  }, [loading]);

  // Cleanup session when leaving the page
  useEffect(() => {
    const currentSessionId = sessionId;
    return () => {
      if (currentSessionId) {
        // Fire-and-forget delete - don't await
        axios.delete(`${API_BASE_URL}/chat/session/${currentSessionId}`)
          .catch(err => console.log('Session cleanup on unmount:', err.message));
      }
    };
  }, [sessionId]);

  // Keep the ref in sync so the pagehide handler (registered once) always sees
  // the current session id.
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // Best-effort teardown on a HARD window/tab close. The on-unmount DELETE above
  // is a fire-and-forget axios call that the browser cancels when the page is torn
  // down, so a hard close would leave the backend chat session hung (and a ghost
  // "live call" in the Call Monitor) until the server-side idle reaper reclaims it.
  // `pagehide` + a keepalive `fetch` survive the teardown, so the DELETE — and its
  // per-practice `call_ended` broadcast — fire immediately on close.
  useEffect(() => {
    const teardown = () => {
      const sid = sessionIdRef.current;
      if (!sid) return;
      const token = getAuthToken();
      try {
        fetch(`${API_BASE_URL}/chat/session/${sid}`, {
          method: 'DELETE',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          keepalive: true,
        }).catch(() => {});
      } catch (_) { /* nothing more we can do during page teardown */ }
    };
    window.addEventListener('pagehide', teardown);
    return () => window.removeEventListener('pagehide', teardown);
  }, []);

  // Send message to chat API
  const sendMessage = async () => {
    if (!inputValue.trim() || loading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setLoading(true);
    setError('');

    // Optimistically add user message to conversation
    setConversation(prev => [...prev, { role: 'user', content: userMessage }]);

    try {
      const response = await axios.post(`${API_BASE_URL}/chat/message`, {
        message: userMessage,
        session_id: sessionId,
        practice_id: practiceId  // multi-tenant: test against the selected practice
      });

      const { session_id, messages, conversation_history } = response.data;

      // Store session ID for subsequent messages
      if (!sessionId && session_id) {
        setSessionId(session_id);
      }

      // Update conversation from server response
      // Filter to only show user and assistant messages
      const filteredHistory = conversation_history.filter(
        msg => msg.role === 'user' || msg.role === 'assistant'
      );
      setConversation(filteredHistory);

    } catch (err) {
      // The backend chat session may have been reclaimed by the idle reaper (or is
      // otherwise gone) -> the send 404s. Transparently start a fresh session and
      // retry the same message once so the tester just keeps typing.
      if (err.response?.status === 404 && sessionId) {
        setSessionId(null);
        sessionIdRef.current = null;
        try {
          const retry = await axios.post(`${API_BASE_URL}/chat/message`, {
            message: userMessage,
            session_id: null,
            practice_id: practiceId
          });
          const { session_id: newId, conversation_history } = retry.data;
          if (newId) setSessionId(newId);
          setConversation(
            conversation_history.filter(m => m.role === 'user' || m.role === 'assistant')
          );
          return;  // handled — skip the error path below
        } catch (retryErr) {
          console.error('Retry after 404 failed:', retryErr);
          setError(retryErr.response?.data?.detail || 'Failed to send message. Please try again.');
          setConversation(prev => prev.slice(0, -1));
          return;
        }
      }
      console.error('Error sending message:', err);
      setError(err.response?.data?.detail || 'Failed to send message. Please try again.');
      // Remove the optimistic user message on error
      setConversation(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
      // Focus handled by useEffect on loading change
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Reset/New chat
  const handleNewChat = async () => {
    if (sessionId) {
      try {
        await axios.post(`${API_BASE_URL}/chat/reset/${sessionId}`);
      } catch (err) {
        console.error('Error resetting session:', err);
      }
    }
    setSessionId(null);
    setConversation([]);
    setError('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Format message role for display
  const formatRole = (role) => {
    if (role === 'user') return 'You';
    if (role === 'assistant') return 'AI Assistant';
    return role;
  };

  return (
    <Layout>
      <div className="chat-page-main" style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h1 className="h1">Chat (for Devs)</h1>
          <button
            onClick={handleNewChat}
            className="btn btn-secondary"
            style={{ padding: '0.5rem 1rem' }}
          >
            New Chat
          </button>
        </div>

        {/* Session Info */}
        <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#666' }}>
          {sessionId ? (
            <span>Session: {sessionId.substring(0, 8)}...</span>
          ) : (
            <span>No active session - send a message to start</span>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="error-message" style={{ marginBottom: '1rem' }}>
            <p>{error}</p>
          </div>
        )}

        {/* Conversation Display */}
        <div className="conversation-container card" style={{
          minHeight: 400,
          maxHeight: 500,
          width: '100%',
          overflowY: 'auto',
          position: 'relative',
          padding: '1rem',
          marginBottom: '1rem'
        }}>
          {conversation.length > 0 ? (
            <>
              {conversation.map((message, index) => (
                <div key={index} className={`message ${message.role === 'assistant' ? 'assistant' : 'user'}`}>
                  <div className="message-header">{formatRole(message.role)}</div>
                  <div className="message-content">{message.content}</div>
                </div>
              ))}
              {loading && (
                <div className="message assistant">
                  <div className="message-header">AI Assistant</div>
                  <div className="message-content" style={{ fontStyle: 'italic', color: '#888' }}>
                    Thinking...
                  </div>
                </div>
              )}
              <div ref={conversationEndRef} />
            </>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              fontSize: '1.1rem',
              color: '#888',
              textAlign: 'center'
            }}>
              Start a conversation by typing a message below
            </div>
          )}
        </div>

        {/* Input Area */}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            disabled={loading}
            style={{
              flex: 1,
              padding: '0.75rem 1rem',
              fontSize: '1rem',
              border: '1px solid #ccc',
              borderRadius: '4px',
              outline: 'none'
            }}
          />
          <button
            onClick={sendMessage}
            disabled={loading || !inputValue.trim()}
            className="btn btn-primary"
            style={{
              padding: '0.75rem 1.5rem',
              opacity: loading || !inputValue.trim() ? 0.6 : 1
            }}
          >
            {loading ? 'Sending...' : 'Send'}
          </button>
        </div>

        {/* Help Text */}
        <div style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#888' }}>
          <p>This chat interface bypasses the phone/audio layer for testing LLM logic directly.</p>
          <p>All messages go through the same processing pipeline as phone calls.</p>
        </div>
      </div>
    </Layout>
  );
};

export default ChatPage;
